/* -------------------------------------------------------------------------- */
/*                         SlidingPaneVideoPlayer                           */
/* -------------------------------------------------------------------------- */

import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import SlidingPane from 'react-sliding-pane';
import { Row, Col, Alert, FormGroup, Label, Button, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Spinner } from 'reactstrap';
import Icon from 'utility/icomoon';
// Using absolute path for imports to avoid TypeScript issues
import VideoPlayerComponent from 'views/screens/Units/views/SlidingPaneVideoPlayer/VideoPlayerComponent';
import VideoPlayerStats from 'views/screens/Units/views/SlidingPaneVideoPlayer/VideoPlayerStats';
import { useVideoPlayer } from '../../hooks/useVideoPlayer';
import useBandwidthControl from '../../hooks/useBandwidthControl';
import { useChannelNames } from 'views/screens/Video/VideoEventsHistory/hooks/useChannelNames';

interface Props {
  isOpenSlidingPane: boolean;
  closeSlidingPane: () => void;
  unit?: any;
  width?: string;
}

const SlidingPaneVideoPlayer: React.FC<Props> = ({
  isOpenSlidingPane,
  closeSlidingPane,
  unit,
  width = '45rem'
}) => {
  const intl = useIntl();
  const [selectedChannel, setSelectedChannel] = useState<string>('1');
  const [currentActiveChannel, setCurrentActiveChannel] = useState<number | null>(null);
  const [channelDropdownOpen, setChannelDropdownOpen] = useState<boolean>(false);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false); // Para mostrar estado de transición
  const [isStoppingStream, setIsStoppingStream] = useState<boolean>(false); // Para distinguir entre iniciar y detener

  // Obtener el IMEI del dispositivo
  const deviceImei = unit?.deviceIdentifier;

  // Usar el hook personalizado para gestionar el reproductor
  const {
    isLoading,
    error,
    streamUrl,
    isStreaming,
    stats,
    totalBytesLoaded,
    startStream,
    stopStream,
    switchChannel,
    updateStats,
    updateBytesLoaded,
    setIsStreaming,
    setStreamUrl,
    setCurrentChannel
  } = useVideoPlayer({ imei: deviceImei, brandId: unit?.brandId, deviceModelId: unit?.deviceModelId });

  // Hook para obtener nombres personalizados de canales y canales disponibles
  const { 
    availableChannels: channelsFromUnit, 
    getChannelName, 
    isLoading: isLoadingChannelNames,
    videoStreamingChannels 
  } = useChannelNames({
    unitId: unit?.unitId
  });

  // Debug log para verificar canales disponibles
  useEffect(() => {
    console.log('🎬 SlidingPaneVideoPlayer - Channel data:', {
      unitId: unit?.unitId,
      deviceImei,
      channelsFromUnit,
      videoStreamingChannels,
      isLoadingChannelNames
    });
  }, [unit?.unitId, deviceImei, channelsFromUnit, videoStreamingChannels, isLoadingChannelNames]);

  const handleBandwidthExceeded = () => {
    setCurrentActiveChannel(null);
    setIsTransitioning(false); // Desactivar estado de transición
    setIsStoppingStream(false); // Resetear estado
    setIsStreaming(false);
    setStreamUrl(null);
    setCurrentChannel(null);
  };

  const { data, state, sendBandwidthRequest } = useBandwidthControl(isOpenSlidingPane,handleBandwidthExceeded);

  // Manejo de canales: usando función optimizada switchChannel
  const handleChannelSwitch = async (channel: number) => {
    if (isLoading || isTransitioning) return;

    // Si ya está reproduciendo el mismo canal, no hacer nada
    if (currentActiveChannel === channel && isStreaming) {
      console.log(`Channel ${channel} is already active`);
      setChannelDropdownOpen(false);
      return;
    }

    setSelectedChannel(String(channel));
    setChannelDropdownOpen(false); // Cerrar dropdown al seleccionar
    setIsTransitioning(true); // Activar estado de transición

    console.log(`Switching to channel ${channel} on IMEI ${deviceImei}`);

    try {
      // Usar la función optimizada switchChannel que maneja internamente stop->start sin throttling
      if (currentActiveChannel !== null) {
        setIsStoppingStream(true); // Indicar que estamos deteniendo
      }
      
      const success = await switchChannel(channel);
      
      setIsStoppingStream(false); // Ya no estamos deteniendo
      
      if (success) {
        setCurrentActiveChannel(channel);
        console.log(`Successfully switched to channel ${channel}`);
      } else {
        console.error(`Failed to switch to channel ${channel}`);
        setCurrentActiveChannel(null);
      }
    } catch (error) {
      console.error(`Error during channel switch:`, error);
      setCurrentActiveChannel(null);
    } finally {
      setIsTransitioning(false); // Desactivar estado de transición
      setIsStoppingStream(false); // Resetear estado
    }
  };

  // Detener la transmisión
  const handleStopStream = async () => {
    setIsTransitioning(true); // Activar estado de transición
    setIsStoppingStream(true); // Indicar que estamos deteniendo
    await stopStream();
    setCurrentActiveChannel(null);
    setIsTransitioning(false); // Desactivar estado de transición
    setIsStoppingStream(false); // Resetear estado
  };

  // Función para alternar el dropdown
  const toggleChannelDropdown = () => {
    if (!isLoading && !isTransitioning && !isLoadingChannelNames) {
      setChannelDropdownOpen(!channelDropdownOpen);
    }
  };

  // Función para obtener el texto del estado actual
  const getDropdownText = () => {
    if (isLoading || isTransitioning) {
      return 'Procesando...';
    }
    
    if (currentActiveChannel && isStreaming) {
      const channelName = getChannelName(currentActiveChannel);
      return `${channelName} (Activo)`;
    }
    
    const channelName = getChannelName(parseInt(selectedChannel));
    return channelName;
  };

  // Función para obtener el color del dropdown según el estado
  const getDropdownColor = () => {
    if (isLoading || isTransitioning) {
      return 'warning';
    }
    
    if (currentActiveChannel && isStreaming) {
      return 'success';
    }
    
    return 'primary';
  };

  // Al cerrar el panel, asegurarse de detener la transmisión del canal activo
  const handleClose = () => {
    if (isStreaming && currentActiveChannel !== null) {
      stopStream().then(() => {
        setCurrentActiveChannel(null);
        closeSlidingPane();
      });
    } else {
      closeSlidingPane();
    }
  };

  // Limpiar estado cuando cambia la unidad
  useEffect(() => {
    setCurrentActiveChannel(null);
    setSelectedChannel('1');
  }, [deviceImei]);

  // Detener el stream cuando se cierra el panel
  useEffect(() => {
    if (!isOpenSlidingPane && isStreaming && currentActiveChannel !== null) {
      stopStream().then(() => {
        setCurrentActiveChannel(null);
      });
    }
  }, [isOpenSlidingPane, isStreaming, currentActiveChannel, stopStream]);

  useEffect(() => {
  if (totalBytesLoaded) {
    sendBandwidthRequest( totalBytesLoaded );
  }
}, [totalBytesLoaded]);

  return (
    <SlidingPane
      className="custom-sliding-pane"
      overlayClassName="custom-overlay-sliding-pane"
      isOpen={isOpenSlidingPane}
      title={intl.formatMessage({ id: 'units.videoPlayer.title' })}
      subtitle={unit?.unitName || ''}
      onRequestClose={handleClose}
      width={width}
    >
      <div>
        <Row className="mb-3">
          <Col sm="12">
            {error && (
              <Alert color="danger" className="mb-3">
                {error}
              </Alert>
            )}

            {!deviceImei && (
              <Alert color="warning">
                {intl.formatMessage({ id: 'units.videoPlayer.noDeviceId' })}
              </Alert>
            )}

            {deviceImei && (
              <>
                <VideoPlayerComponent
                  streamUrl={streamUrl || undefined}
                  isStreaming={isStreaming}
                  isLoading={isLoading}
                  onStatsUpdate={updateStats}
                  onError={(errorMsg) => console.error('Player error:', errorMsg)}
                  onBytesLoaded={updateBytesLoaded}
                />


                {/* Indicador de estado del proceso */}
                {(isLoading || isTransitioning) && (
                  <div className="mt-2 mb-2">
                    <Alert color="info" className="d-flex align-items-center mb-0">
                      <Spinner size="sm" className="mr-2" />
                      <span>
                        {isStoppingStream ? 'Deteniendo transmisión...' :
                         isLoading && isTransitioning ? `Cambiando a ${getChannelName(parseInt(selectedChannel))}...` : 
                         isLoading ? 'Cargando...' : 
                         'Procesando solicitud...'}
                      </span>
                    </Alert>
                  </div>
                )}

                {/* Indicador de estado activo */}
                {isStreaming && currentActiveChannel && !isLoading && !isTransitioning && (
                  <div className="mt-2 mb-2">
                    <Alert color="success" className="d-flex align-items-center mb-0">
                      <Icon icon="CheckCircle" size={16} className="mr-2" />
                      <span>
                        Transmisión activa en {getChannelName(currentActiveChannel)}
                      </span>
                    </Alert>
                  </div>
                )}

                {/* Toolbar con dropdown de canales debajo del video player */}
                <div className="mt-3">
                  {/* Primera fila: Dropdown de canales */}
                  <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: '12px' }}>
                    <div className="d-flex align-items-center">
                      <Dropdown
                        isOpen={channelDropdownOpen}
                        toggle={toggleChannelDropdown}
                        disabled={isLoading || isTransitioning || isLoadingChannelNames}
                      >
                        <DropdownToggle
                          caret
                          color={getDropdownColor()}
                          size="sm"
                          style={{ minWidth: '140px' }}
                          disabled={isLoading || isTransitioning || isLoadingChannelNames}
                        >
                          {(isLoading || isTransitioning || isLoadingChannelNames) && (
                            <Spinner size="sm" className="mr-2" />
                          )}
                          {!(isLoading || isTransitioning || isLoadingChannelNames) && (
                            <Icon icon="Video" size={16} className="mr-1" />
                          )}
                          {getDropdownText()}
                        </DropdownToggle>
                        <DropdownMenu>
                          {channelsFromUnit.length === 0 ? (
                            <DropdownItem disabled>
                              <Icon icon="AlertTriangle" size={16} className="mr-2" color="#ffc107" />
                              No hay canales disponibles
                            </DropdownItem>
                          ) : (
                            channelsFromUnit.map((channel) => {
                              const isActive = currentActiveChannel === channel && isStreaming;
                              const shouldDisableChannel = isLoading || isTransitioning || isLoadingChannelNames;
                              const channelName = getChannelName(channel);
                              
                              return (
                                <DropdownItem
                                  key={`channel-${channel}`}
                                  onClick={() => handleChannelSwitch(channel)}
                                  disabled={shouldDisableChannel}
                                  style={{
                                    backgroundColor: isActive ? '#e6f3ff' : 'transparent',
                                    color: isActive ? '#007bff' : shouldDisableChannel ? '#6c757d' : 'inherit',
                                    fontWeight: isActive ? 'bold' : 'normal'
                                  }}
                                >
                                  <Icon 
                                    icon={isActive ? "CheckCircle" : "Video"} 
                                    size={16} 
                                    className="mr-2" 
                                    color={isActive ? "#28a745" : shouldDisableChannel ? "#6c757d" : "#6c757d"}
                                  />
                                  {channelName}
                                  {isActive && (
                                    <span className="badge badge-success ml-2">
                                      Activo
                                    </span>
                                  )}
                                </DropdownItem>
                              );
                            })
                          )}
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                    
                    {/* Botón detener alineado a la derecha */}
                    <Button
                      color="danger"
                      size="sm"
                      disabled={!isStreaming || isLoading || isTransitioning}
                      onClick={handleStopStream}
                    >
                      {isTransitioning && isStoppingStream ? (
                        <Spinner size="sm" className="mr-1" />
                      ) : (
                        <Icon icon="Stop" size={16} className="mr-1" />
                      )}
                      {intl.formatMessage({ id: 'units.videoPlayer.stop' })}
                    </Button>
                  </div>
                </div>

                {/* Video Player Stats - Solo bytes totales */}
                {totalBytesLoaded !== undefined && (
                  <div className="mt-3">
                    <VideoPlayerStats stats={{ totalBytes: totalBytesLoaded }} />
                  </div>
                )}
              </>
            )}
          </Col>
        </Row>
      </div>
    </SlidingPane>
  );
};

export default SlidingPaneVideoPlayer;
